import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogContent, MatDialogRef} from "@angular/material/dialog";
import {FormControl, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {NgForOf, NgIf} from "@angular/common";
import {HttpClient} from "@angular/common/http";
import {firstValueFrom} from "rxjs";
import {environment} from "../../../../environments/environment";
import {WorkspaceService} from "../../services/workspace.service";
import {AuthService} from "../../services/auth.service";
import {ModalHeaderComponent} from "../../layout/modal-header/modal-header.component";
import {AiCodeAssistRequest, AiCodeAssistResponse} from "../../interfaces/computation/ai-code-assist.interface";
import {AutofocusDirective} from "../../directives/autofocus/autofocus.directive";

export interface AiCodeAssistData {
  currentCode: string
  libraries: { name: string, alias: string }[]
  aliases: { alias: string, type: string, sample: unknown }[]
  incomingFields: string[]
  previousTransformations: string[]
}

@Component({
  selector: 'app-ai-code-assist',
  imports: [MatDialogContent, ModalHeaderComponent, FormsModule, ReactiveFormsModule, NgIf, NgForOf, AutofocusDirective],
  templateUrl: './ai-code-assist.component.html',
  styleUrl: './ai-code-assist.component.scss'
})
export class AiCodeAssistComponent {
  code: string
  history: { request: string, codeAfter: string }[] = []
  descriptionControl = new FormControl('')
  loading = false
  error = ''

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AiCodeAssistData,
    public dialogRef: MatDialogRef<AiCodeAssistComponent>,
    private http: HttpClient,
    private workspaceSvc: WorkspaceService,
    private authSvc: AuthService
  ) {
    this.code = this.data.currentCode ?? ''
  }

  get hasCode() {
    return !!this.code
  }

  async submit() {
    const description = this.descriptionControl.value ?? ''
    if (!description.trim()) { return }
    this.loading = true
    this.error = ''
    try {
      const payload: AiCodeAssistRequest = {
        mode: this.hasCode ? 'edit' : 'generate',
        description,
        currentCode: this.code,
        history: this.history,
        libraries: this.data.libraries,
        aliases: this.data.aliases,
        incomingFields: this.data.incomingFields,
        previousTransformations: this.data.previousTransformations
      }
      const token = await this.authSvc.getIdToken()
      const response = await firstValueFrom(this.http.post(
        `${environment.core}assistants/${this.workspaceSvc.workspaceId}/generate-transformation-code`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      )) as AiCodeAssistResponse
      this.history.push({request: description, codeAfter: response.code})
      this.code = response.code
      this.descriptionControl.setValue('')
    } catch (error: any) {
      this.error = error.error?.message ?? 'Something went wrong generating the code'
    }
    this.loading = false
  }

  startOver() {
    this.code = ''
    this.history = []
    this.error = ''
    this.descriptionControl.setValue('')
  }

  useCode() {
    this.dialogRef.close({code: this.code} as AiCodeAssistResponse)
  }

  cancel() {
    this.dialogRef.close(null)
  }
}
